import { useEffect } from "react";
import Footer from "../components/Footer";
import PublicNav from "../components/PublicNav";

const SECTIONS = [
  {
    title: "1. Who we are",
    content: `Joborigo is operated by Stefan Asanin, trading as Joborigo (enskild firma), registered in Sweden. We are the data controller for personal data collected through joborigo.com.

Contact: support@joborigo.com`,
  },
  {
    title: "2. What data we collect",
    content: `We collect the following categories of personal data:

Job seekers: full name, email address, job application data (company names, job titles, statuses, notes), professional timeline entries (work history, education, certifications), resume data, arena profile data, and usage data.

Employers and agencies: company name, company website, work email address, API usage data, and candidate integration data.

Signup context (all users): the approximate country and city derived from your IP address at the moment you register, the page on our site where you arrived, and the referring website if your browser sent one. This is used to understand where our users come from. We do not store the IP address itself alongside this, and we do not use it to identify you.

Technical data (all users): IP addresses and request logs, collected for security, abuse prevention, and rate limiting. These are retained for a limited time and not used to identify individuals beyond what is necessary to operate the service.

Error diagnostics: when something goes wrong, technical details of the failure are recorded so we can fix it. These may incidentally include data you were working with at the time.

We do not collect gender, age, nationality, or photo data for arena profiles. Anonymity there is by design.`,
  },
  {
    title: "3. Why we collect it (legal basis)",
    content: `We process your data on the following legal bases under GDPR Article 6:

Contract performance (Article 6(1)(b)): to provide the Joborigo service you signed up for, including tracking applications, sending job positions, and pushing status updates.

Legitimate interests (Article 6(1)(f)): to improve the service, detect abuse, ensure security, and understand where our users come from.

Legal obligation (Article 6(1)(c)): to comply with applicable Swedish and EU law.

Consent (Article 6(1)(a)): for any processing that requires your explicit consent, for example marketing communications if introduced in future. You may withdraw consent at any time by contacting us at support@joborigo.com.

We do not use your data for advertising. We do not sell your data to third parties, and we do not buy personal data about anyone from third parties.`,
  },
  {
    title: "4. How we use your data",
    content: `Your data is used to:
• Provide and operate the Joborigo platform
• Send transactional emails (verification, password reset, notifications)
• Enable employers and agencies to push status updates to candidates
• Enable the anonymous candidate arena
• Generate aggregated, anonymised usage metrics
• Diagnose and fix technical faults
• Comply with legal obligations`,
  },
  {
    title: "5. Data sharing",
    content: `We share data with the following categories of recipients, all of whom process it on our instructions:

Hosting: Vercel (frontend delivery) and Render (application backend).

Database: Neon (PostgreSQL, EU region).

Email: Resend (transactional email delivery).

Error monitoring: Sentry (technical fault diagnostics). Sentry is a US-based provider and participates in the EU-US Data Privacy Framework, with Standard Contractual Clauses as a fallback mechanism.

Location lookup: MaxMind GeoLite2, used locally on our own servers to derive an approximate country and city from an IP address at signup. No data is sent to MaxMind.

Payments: Paddle (Merchant of Record for subscriptions, acting as the seller of record to end customers and processing payment data independently).

We have Data Processing Agreements in place with our subprocessors, in line with GDPR Article 28. Paddle is not a subprocessor: as Merchant of Record it acts as an independent data controller for payment data, governed by their Data Sharing Addendum.

We do not share personal data with advertisers, data brokers, or any third party beyond what is necessary to operate the service.`,
  },
  {
    title: "6. Data retention",
    content: `Active accounts: data is retained for as long as your account is active.

Deleted accounts: when you delete your account, your data is soft-deleted immediately, preventing login and hiding your data, and permanently purged from our live database after 30 days. This is our implementation of the right to erasure.

Backups: encrypted database backups may retain your data for up to an additional 30 days after live-database deletion, after which all copies are removed. In practice all of your data is gone within 60 days of deletion at most.

Error diagnostics: technical fault records are retained for a limited period and then deleted automatically.`,
  },
  {
    title: "7. Your rights under GDPR",
    content: `You have the following rights regarding your personal data:

Right of access (Article 15): request a copy of your data.
Right to rectification (Article 16): correct inaccurate data.
Right to erasure (Article 17): delete your account and data.
Right to data portability (Article 20): download your data as JSON from Account Settings.
Right to object (Article 21): object to processing based on legitimate interests.
Right to restriction (Article 18): request restriction of processing.
Right to withdraw consent (Article 7(3)): where processing is based on consent, you may withdraw it at any time without affecting the lawfulness of processing prior to withdrawal.

To exercise any of these rights, contact support@joborigo.com. We will respond within 30 days.

You also have the right to lodge a complaint with the Swedish Authority for Privacy Protection (Integritetsskyddsmyndigheten, IMY) at imy.se.`,
  },
  {
    title: "8. Cookies and local storage",
    content: `Joborigo uses the following:

Essential cookies: authentication session management (refresh token). These are required for the service to function and cannot be opted out of.

Local storage: your theme preference, and the page you arrived on, kept in your browser for the duration of your visit so we can record where a signup came from.

We do not use advertising cookies, tracking pixels, or third-party analytics cookies.`,
  },
  {
    title: "9. Security",
    content: `We implement appropriate technical and organisational measures to protect your data, including:
• Encrypted data in transit (HTTPS/TLS)
• Hashed passwords (bcrypt)
• HTTP-only, secure cookies for session tokens
• Rate limiting on authentication endpoints
• Filtering of credentials and secrets from error diagnostics before they leave our servers
• Regular security reviews`,
  },
  {
    title: "10. International transfers",
    content: `Our database is hosted in the European Union. Our hosting, email and error monitoring providers are US-based and process data in the United States.

Those transfers are covered by the EU-US Data Privacy Framework, with Standard Contractual Clauses approved by the European Commission as a fallback mechanism, providing an adequate level of protection in line with GDPR Article 46.`,
  },
  {
    title: "11. Changes to this policy",
    content: `We may update this Privacy Policy from time to time. Material changes will be communicated via email to registered users at least 14 days before taking effect, giving you time to review the changes and, if you do not accept them, to delete your account before they apply. The date at the top of this page reflects the most recent update.`,
  },
  {
    title: "12. Contact",
    content: `For privacy-related questions or to exercise your rights:

Email: support@joborigo.com
Controller: Stefan Asanin, Joborigo (enskild firma), Sweden`,
  },
];

export default function Privacy() {
  useEffect(() => {
    document.title = "Joborigo: Privacy Policy";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <PublicNav />

      <main className="flex-1 max-w-3xl mx-auto px-6 py-16 w-full">
        <div className="mb-12">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            Legal
          </p>
          <h1 className="text-4xl font-extrabold mb-4">Privacy Policy</h1>
          <p className="text-gray-400 text-sm">
            Last updated: September 2026 · Effective immediately
          </p>
          <p className="text-gray-500 dark:text-gray-400 mt-4 leading-relaxed">
            This Privacy Policy explains how Joborigo collects, uses, and
            protects your personal data in accordance with the EU General Data
            Protection Regulation (GDPR) and applicable Swedish law.
          </p>
        </div>

        <div className="flex flex-col gap-10">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                {section.title}
              </h2>
              <div className="text-gray-500 dark:text-gray-400 leading-relaxed whitespace-pre-line text-sm">
                {section.content}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-gray-100 dark:border-white/10">
          <p className="text-sm text-gray-400">
            Questions about this policy? Contact us at{" "}
            <a
              href="mailto:support@joborigo.com"
              className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
            >
              support@joborigo.com
            </a>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
